import React, { useRef, useEffect, useState, useCallback } from 'react';
import fondo from '../../assets/img/fondo_ping.png';

type Difficulty = 'easy' | 'medium' | 'hard';
type GameStatus = 'stopped' | 'playing' | 'paused';

interface GameState {
  playerY: number;
  aiY: number;
  ballX: number;
  ballY: number;
  ballSpeedX: number;
  ballSpeedY: number;
  playerScore: number;
  aiScore: number;
}

interface DifficultySettings {
  aiSpeed: number;
  ballSpeedMultiplier: number;
  paddleHeight: number;
}

const difficultySettings: Record<Difficulty, DifficultySettings> = {
  easy: { aiSpeed: 3, ballSpeedMultiplier: 1, paddleHeight: 100 },
  medium: { aiSpeed: 5, ballSpeedMultiplier: 1.5, paddleHeight: 80 },
  hard: { aiSpeed: 7, ballSpeedMultiplier: 2, paddleHeight: 60 },
};

const ALTURA = '270px';
const ANCHO = '400px';


export const PingPongGame: React.FC<{}> = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const backgroundImageRef = useRef<HTMLImageElement | null>(null);
  const gameStateRef = useRef<GameState>({
    playerY: 150,
    aiY: 150,
    ballX: 300,
    ballY: 200,
    ballSpeedX: 5,
    ballSpeedY: 5,
    playerScore: 0,
    aiScore: 0,
  });
  const [difficulty, setDifficulty] = useState<Difficulty>('medium');
  const [score, setScore] = useState({ player: 0, ai: 0 });
  const [gameStatus, setGameStatus] = useState<GameStatus>('stopped');
  const animationFrameId = useRef<number | null>(null);

  const paddleWidth = 10;
  const ballSize = 10;

  function resetBall() {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const state = gameStateRef.current;
    state.ballX = canvas.width / 2;
    state.ballY = canvas.height / 2;
    state.ballSpeedX = 5 * difficultySettings[difficulty].ballSpeedMultiplier * (Math.random() > 0.5 ? 1 : -1);
    state.ballSpeedY = 5 * difficultySettings[difficulty].ballSpeedMultiplier * (Math.random() > 0.5 ? 1 : -1);
  }

  useEffect(() => {
    const image = new Image();
    image.src = fondo; // Reemplaza con la ruta de tu imagen
    image.onload = () => {
      backgroundImageRef.current = image;
    };
  }, []);

  function gameLoop() {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const state = gameStateRef.current;
    const { aiSpeed, paddleHeight } = difficultySettings[difficulty];

    // Update ball position
    state.ballX += state.ballSpeedX;
    state.ballY += state.ballSpeedY;

    // Ball collision with top and bottom walls
    if (state.ballY <= 0 || state.ballY >= canvas.height - ballSize) {
      state.ballSpeedY = -state.ballSpeedY;
    }

    // Ball collision with paddles
    if (
      (state.ballX <= paddleWidth && state.ballY > state.playerY && state.ballY < state.playerY + paddleHeight) ||
      (state.ballX >= canvas.width - paddleWidth - ballSize && state.ballY > state.aiY && state.ballY < state.aiY + paddleHeight)
    ) {
      state.ballSpeedX = -state.ballSpeedX;
    }

    // Score points
    if (state.ballX <= 0) {
      state.aiScore++;
      setScore(prev => ({ ...prev, ai: state.aiScore }));
      resetBall();
    } else if (state.ballX >= canvas.width - ballSize) {
      state.playerScore++;
      setScore(prev => ({ ...prev, player: state.playerScore }));
      resetBall();
    }

    // AI movement
    if (state.ballY > state.aiY + paddleHeight / 2) {
      state.aiY = Math.min(state.aiY + aiSpeed, canvas.height - paddleHeight);
    } else if (state.ballY < state.aiY + paddleHeight / 2) {
      state.aiY = Math.max(state.aiY - aiSpeed, 0);
    }

    // Clear canvas
    // ctx.fillStyle = 'green';
    if (backgroundImageRef.current)
      ctx.drawImage(backgroundImageRef.current, 0, 0, canvas.width, canvas.height);
    else {
      ctx.fillStyle = 'green';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // Draw paddles
    ctx.fillStyle = 'white';
    ctx.fillRect(0, state.playerY, paddleWidth, paddleHeight);
    ctx.fillRect(canvas.width - paddleWidth, state.aiY, paddleWidth, paddleHeight);

    // Draw ball
    ctx.beginPath();
    ctx.arc(state.ballX, state.ballY, ballSize / 2, 0, Math.PI * 2);
    ctx.fill();

    // Draw scores
    ctx.font = '30px Arial Black';
    ctx.fillStyle = state.aiScore == state.playerScore ? '#ffffff' :
      state.playerScore > state.aiScore ? '#00FF00' : '#FF0000'; // Color verde para el score ganador y rojo para score perdedor
    ctx.fillText(state.playerScore.toString(), canvas.width / 4, 50);

    ctx.fillStyle = state.aiScore == state.playerScore ? '#ffffff' :
      state.aiScore > state.playerScore ? '#00FF00' : '#FF0000'; // Color verde para el score ganador y rojo para score perdedor
    ctx.fillText(state.aiScore.toString(), 3 * canvas.width / 4, 50);

    if (gameStatus === 'playing') {
      animationFrameId.current = requestAnimationFrame(gameLoop);
    }
  }

  const resetGame = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const state = gameStateRef.current;
    const { paddleHeight } = difficultySettings[difficulty];
    state.playerY = canvas.height / 2 - paddleHeight / 2;
    state.aiY = canvas.height / 2 - paddleHeight / 2;
    state.playerScore = 0;
    state.aiScore = 0;
    setScore({ player: 0, ai: 0 });
    resetBall();
  }, [difficulty]);

  const startGame = useCallback(() => {
    // resetGame();
    setGameStatus('playing');
  }, [resetGame]);

  const pauseGame = useCallback(() => {
    setGameStatus(prev => prev === 'playing' ? 'paused' : 'playing');
  }, []);

  const stopGame = useCallback(() => {
    setGameStatus('stopped');
    resetGame();
  }, [resetGame]);

  const changeDifficulty = useCallback((newDifficulty: Difficulty) => {
    setDifficulty(newDifficulty);
    if (gameStatus !== 'stopped') {
      resetGame();
    }
  }, [gameStatus, resetGame]);

  useEffect(() => {
    if (gameStatus === 'playing') {
      animationFrameId.current = requestAnimationFrame(gameLoop);
    } else if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [gameStatus]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (gameStatus !== 'playing') return;

      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const mouseY = e.clientY - rect.top;
      const { paddleHeight } = difficultySettings[difficulty];
      gameStateRef.current.playerY = Math.max(0, Math.min(mouseY - paddleHeight / 2, canvas.height - paddleHeight));
    };

    window.addEventListener('mousemove', handleMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, [gameStatus, difficulty]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <canvas
        ref={canvasRef}
        width={ANCHO}
        height={ALTURA}
        style={{ backgroundColor: 'Menu', marginBottom: '20px' }}
      />
      <div style={{ marginBottom: '10px' }}>
        <button onClick={startGame} disabled={gameStatus === 'playing'} style={{ marginRight: '15px' }}>Iniciar</button>
        <button onClick={pauseGame} disabled={gameStatus === 'stopped'} style={{ marginRight: '15px' }}>
          {gameStatus === 'paused' ? 'Reanudar' : 'Pausar'}
        </button>
        <button onClick={stopGame} disabled={gameStatus === 'stopped'} style={{ marginRight: '15px' }}>Detener</button>
      </div>
      <div style={{ marginBottom: '10px' }}>
        {(['easy', 'medium', 'hard'] as Difficulty[]).map((d) => (
          <button
            key={d}
            onClick={() => changeDifficulty(d)}
            style={{
              backgroundColor: difficulty === d ? '#4CAF50' : '#008CBA',
              color: 'white',
              padding: '10px 20px',
              margin: '0 5px',
              border: 'none',
              cursor: 'pointer'
            }}
          >
            {d.charAt(0).toUpperCase() + d.slice(1)}
          </button>
        ))}
      </div>
      {/* <div>Jugador: {score.player} - IA: {score.ai}</div> */}
    </div>
  );
};

export default PingPongGame;